import { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Eye, Image, Upload, X, Check, FolderOpen, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available product images in the public folder
const AVAILABLE_IMAGES = [
  '/images/products/IMG_6195.jpg',
  '/images/products/IMG_6198.jpg',
  '/images/products/IMG_6203.jpg',
  '/images/products/IMG_6205.jpg',
  '/images/products/IMG_6207.jpg',
  '/images/products/IMG_6209.jpg',
  '/images/products/IMG_6215.jpg',
  '/images/products/IMG_6219.jpg',
  '/images/products/IMG_6223.jpg',
  '/images/products/IMG_6227.jpg',
  '/images/products/IMG_6230.jpg',
  '/images/products/IMG_6234.jpg',
  '/images/products/IMG_6237.jpg',
  '/images/products/IMG_6240.jpg',
  '/images/products/IMG_6243.jpg',
  '/images/products/IMG_6246.jpg',
  '/images/products/IMG_6249.jpg',
  '/images/products/IMG_6252.jpg',
  '/images/products/IMG_6255.jpg',
  '/images/products/IMG_6258.jpg',
  '/images/products/IMG_6261.jpg',
  '/images/products/IMG_6264.jpg',
  '/images/products/IMG_6267.jpg',
  '/images/products/IMG_6270.jpg',
  '/images/products/IMG_6273.jpg',
  '/images/products/IMG_6276.jpg',
  '/images/products/IMG_6279.jpg',
  '/images/products/IMG_6282.jpg',
  '/images/products/IMG_6287.jpg',
  '/images/products/IMG_6290.jpg',
  '/images/products/IMG_6293.jpg',
  '/images/products/IMG_6296.jpg',
  '/images/products/IMG_6301.jpg',
  '/images/products/santorini_collection.jpg',
  '/images/products/santorini_gratitude.jpg',
  '/images/products/desert_sunset_flying.jpg',
  '/images/products/autumn_leaves.jpg',
  '/images/products/beach_with_love.jpg',
  '/images/products/bridal_coffee.jpg',
  '/images/products/artisan_embroidery_woman.jpg',
  '/images/products/artisan_loom_cream.jpg',
  '/images/products/artisan_weaver_man.jpg',
  '/images/products/artisan_yarn_hands.jpg',
  '/images/products/wedding_pillar.jpg',
  '/images/products/beach_sand_white.jpg',
  '/images/products/bridal_maroon_gold.jpg',
  '/images/products/zari_white.jpg',
];

// Image Picker Component with Upload
const ImagePicker = ({ selectedImages, onSelect, onClose, token }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(selectedImages || []);
  const [allImages, setAllImages] = useState(AVAILABLE_IMAGES);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('library'); // 'library' or 'upload'

  // Fetch any additional uploaded images
  useEffect(() => {
    fetchUploadedImages();
  }, []);

  const fetchUploadedImages = async () => {
    try {
      const response = await axios.get(`${API}/upload/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const uploadedUrls = response.data.images.map(img => img.url);
      // Merge with existing, avoiding duplicates
      const merged = [...new Set([...AVAILABLE_IMAGES, ...uploadedUrls])];
      setAllImages(merged);
    } catch (error) {
      console.error('Error fetching uploaded images:', error);
    }
  };

  const filteredImages = allImages.filter(img => 
    img.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleImage = (img) => {
    if (selected.includes(img)) {
      setSelected(selected.filter(i => i !== img));
    } else {
      setSelected([...selected, img]);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        uploadedUrls.push(response.data.url);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploadedUrls.length > 0) {
      // Add to allImages and select them
      setAllImages(prev => [...uploadedUrls, ...prev]);
      setSelected(prev => [...prev, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
      setActiveTab('library');
    }

    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const handleConfirm = () => {
    onSelect(selected.length > 0 ? selected : ['']);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background w-full max-w-4xl mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-medium">Select Product Images</h3>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('library')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'library' 
                ? 'border-b-2 border-primary text-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            Image Library ({allImages.length})
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'upload' 
                ? 'border-b-2 border-primary text-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Upload New
          </button>
        </div>

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="p-8 flex-1 flex flex-col items-center justify-center">
            <div className="w-full max-w-md">
              <label 
                className={`flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-none cursor-pointer transition-colors ${
                  uploading ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary hover:bg-secondary/30'
                }`}
              >
                <div className="flex flex-col items-center justify-center py-6">
                  {uploading ? (
                    <>
                      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                      <p className="text-sm text-muted-foreground">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                      <p className="mb-2 text-sm font-medium">Click to upload images</p>
                      <p className="text-xs text-muted-foreground">or drag and drop</p>
                      <p className="text-xs text-muted-foreground mt-2">PNG, JPG, WebP (Max 10MB each)</p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleUpload}
                  disabled={uploading}
                />
              </label>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Uploaded images will appear in your library
              </p>
            </div>
          </div>
        )}

        {/* Library Tab */}
        {activeTab === 'library' && (
          <>
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search images..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-none"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {selected.length} image(s) selected
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                {filteredImages.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => toggleImage(img)}
                    className={`relative aspect-square cursor-pointer border-2 transition-all ${
                      selected.includes(img) 
                        ? 'border-primary ring-2 ring-primary/20' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Product ${idx + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {selected.includes(img) && (
                      <div className="absolute top-1 right-1 w-6 h-6 bg-primary text-white flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-1 truncate">
                      {img.split('/').pop()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="p-4 border-t flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} className="rounded-none">
            Cancel
          </Button>
          <Button onClick={handleConfirm} className="rounded-none bg-primary text-primary-foreground">
            <Check className="w-4 h-4 mr-2" /> Confirm Selection
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const token = localStorage.getItem('chaleur_admin_token');

  // Gifting options
  const GIFT_RECIPIENTS = ['Mother', 'Partner', 'Friend', 'Colleague', 'Self'];
  const GIFT_OCCASIONS = ['Birthday', 'Anniversary', 'Festive', 'Corporate', 'Wedding', 'Thank You', 'Just Because'];
  const GIFT_STYLES = ['Minimal', 'Vibrant', 'Traditional', 'Luxe'];

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    short_description: '',
    category_id: '',
    images: [''],
    variants: [{ id: crypto.randomUUID(), name: 'Default', sku: '', price: 0, inventory_quantity: 10, color: '', material: '', weight: 0.5 }],
    story_behind: '',
    materials_care: '',
    impact_statement: '',
    craft_technique: '',
    tags: [],
    gifting_tags: [],
    // Gifting Guide fields
    gift_recipients: [],
    gift_occasions: [],
    gift_style: '',
    gift_message: '',
    is_featured: false,
    is_trending: false,
    is_limited_edition: false
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [currentPage, search]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products`, {
        params: { page: currentPage, limit: 50, search: search || undefined, include_all: true }
      });
      setProducts(response.data.products || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        collection_ids: []
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created');
      }
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      if (error.response?.status === 401) {
        handleAuthError();
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      slug: product.slug,
      description: product.description || '',
      short_description: product.short_description || '',
      category_id: product.category_id || '',
      images: product.images?.length ? product.images : [''],
      variants: product.variants || [{ id: crypto.randomUUID(), name: 'Default', sku: '', price: 0, inventory_quantity: 10, color: '', material: '', weight: 0.5 }],
      story_behind: product.story_behind || '',
      materials_care: product.materials_care || '',
      impact_statement: product.impact_statement || '',
      craft_technique: product.craft_technique || '',
      tags: product.tags || [],
      gifting_tags: product.gifting_tags || [],
      gift_recipients: product.gift_recipients || [],
      gift_occasions: product.gift_occasions || [],
      gift_style: product.gift_style || '',
      gift_message: product.gift_message || '',
      is_featured: product.is_featured || false,
      is_trending: product.is_trending || false,
      is_limited_edition: product.is_limited_edition || false
    });
    setShowForm(true);
  };

  const handleAuthError = () => {
    localStorage.removeItem('chaleur_admin_token');
    toast.error('Session expired. Please log in again.');
    window.location.href = '/admin/login';
  };

  const handleDelete = async (productId) => {
    if (!productId) {
      toast.error('Invalid product ID');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      if (error.response?.status === 401) {
        handleAuthError();
        return;
      }
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete product';
      console.error('Delete error:', error.response?.status, error.response?.data || error);
      toast.error(errorMsg);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      slug: '',
      description: '',
      short_description: '',
      category_id: categories[0]?.id || '',
      images: [''],
      variants: [{ id: crypto.randomUUID(), name: 'Default', sku: '', price: 0, inventory_quantity: 10, color: '', material: '', weight: 0.5 }],
      story_behind: '',
      materials_care: '',
      impact_statement: '',
      craft_technique: '',
      tags: [],
      gifting_tags: [],
      gift_recipients: [],
      gift_occasions: [],
      gift_style: '',
      gift_message: '',
      is_featured: false,
      is_trending: false,
      is_limited_edition: false
    });
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const toggleGiftOption = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value) 
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-light mb-2">Products</h1>
          <p className="text-muted-foreground">Manage your product catalog</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingProduct(null); setShowForm(true); }}
          className="rounded-none bg-foreground text-primary-foreground"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-none"
        />
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      ) : products.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map(product => (
            <Card key={product.id} className="rounded-none border-gray-200">
              <div className="aspect-square overflow-hidden bg-gray-100">
                <img 
                  src={product.images?.[0] || 'https://images.unsplash.com/photo-1585007326391-d4bfd01fb320?w=400&q=80'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium line-clamp-1 mb-1">{product.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  ₹{product.variants?.[0]?.price?.toLocaleString() || 0}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 rounded-none"
                    onClick={() => handleEdit(product)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-none text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(product.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-muted-foreground mb-4">No products found</p>
          <Button onClick={() => setShowForm(true)} className="rounded-none">
            Add Your First Product
          </Button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={`w-10 h-10 flex items-center justify-center text-sm ${
                currentPage === idx + 1 
                  ? 'bg-foreground text-primary-foreground' 
                  : 'border border-gray-200 hover:border-primary'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      )}

      {/* Product Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Product Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slug: generateSlug(e.target.value) })}
                  className="rounded-none"
                  required
                />
              </div>
              <div>
                <Label className="mb-2 block">Slug</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="rounded-none"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Short Description</Label>
              <Input
                value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                className="rounded-none"
              />
            </div>

            <div>
              <Label className="mb-2 block">Full Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="min-h-[100px] rounded-none"
              />
            </div>

            {/* Category Selection */}
            <div>
              <Label className="mb-2 block">Category *</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="rounded-none">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Craft Technique</Label>
                <Input
                  value={form.craft_technique}
                  onChange={(e) => setForm({ ...form, craft_technique: e.target.value })}
                  placeholder="e.g. Handwoven, Block Print"
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="mb-2 block">Product Images</Label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowImagePicker(true)}
                  className="w-full rounded-none justify-start"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  {form.images[0] ? `${form.images.filter(i => i).length} image(s) selected` : 'Select Images'}
                </Button>
              </div>
            </div>

            {/* Selected Images Preview */}
            {form.images[0] && (
              <div className="p-4 bg-gray-50">
                <Label className="mb-3 block text-sm">Selected Images</Label>
                <div className="flex gap-3 flex-wrap">
                  {form.images.filter(img => img).map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20 group">
                      <img 
                        src={img} 
                        alt={`Selected ${idx + 1}`} 
                        className="w-full h-full object-cover border"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newImages = form.images.filter((_, i) => i !== idx);
                          setForm({ ...form, images: newImages.length ? newImages : [''] });
                        }}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {idx === 0 && (
                        <span className="absolute bottom-0 left-0 right-0 bg-primary text-white text-[9px] text-center py-0.5">
                          Main
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Variant */}
            <div className="p-4 bg-gray-50 space-y-4">
              <h4 className="font-medium">Variant</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="mb-2 block">Price (₹)</Label>
                  <Input
                    type="number"
                    value={form.variants[0].price}
                    onChange={(e) => setForm({
                      ...form,
                      variants: [{ ...form.variants[0], price: parseFloat(e.target.value) || 0 }]
                    })}
                    className="rounded-none"
                    required
                  />
                </div>
                <div>
                  <Label className="mb-2 block">SKU</Label>
                  <Input
                    value={form.variants[0].sku}
                    onChange={(e) => setForm({
                      ...form,
                      variants: [{ ...form.variants[0], sku: e.target.value }]
                    })}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Inventory</Label>
                  <Input
                    type="number"
                    value={form.variants[0].inventory_quantity}
                    onChange={(e) => setForm({
                      ...form,
                      variants: [{ ...form.variants[0], inventory_quantity: parseInt(e.target.value) || 0 }]
                    })}
                    className="rounded-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Color</Label>
                  <Input
                    value={form.variants[0].color}
                    onChange={(e) => setForm({
                      ...form,
                      variants: [{ ...form.variants[0], color: e.target.value }]
                    })}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Material</Label>
                  <Input
                    value={form.variants[0].material}
                    onChange={(e) => setForm({
                      ...form,
                      variants: [{ ...form.variants[0], material: e.target.value }]
                    })}
                    className="rounded-none"
                  />
                </div>
              </div>
            </div>

            {/* Gifting Guide Section */}
            <div className="p-4 bg-amber-50 border border-amber-200 space-y-4">
              <div className="flex items-center gap-2 text-amber-800">
                <Gift className="w-5 h-5" />
                <h4 className="font-medium">Gifting Guide Settings</h4>
              </div>
              <p className="text-sm text-amber-700">Configure how this product appears in the Gifting Guide</p>
              
              <div>
                <Label className="mb-2 block text-sm">Suitable Recipients</Label>
                <div className="flex flex-wrap gap-2">
                  {GIFT_RECIPIENTS.map(recipient => (
                    <button
                      key={recipient}
                      type="button"
                      onClick={() => toggleGiftOption('gift_recipients', recipient)}
                      className={`px-3 py-1.5 text-sm border transition-colors ${
                        form.gift_recipients.includes(recipient)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white border-gray-300 hover:border-primary'
                      }`}
                    >
                      {recipient}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-2 block text-sm">Suitable Occasions</Label>
                <div className="flex flex-wrap gap-2">
                  {GIFT_OCCASIONS.map(occasion => (
                    <button
                      key={occasion}
                      type="button"
                      onClick={() => toggleGiftOption('gift_occasions', occasion)}
                      className={`px-3 py-1.5 text-sm border transition-colors ${
                        form.gift_occasions.includes(occasion)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white border-gray-300 hover:border-primary'
                      }`}
                    >
                      {occasion}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block text-sm">Style Category</Label>
                  <Select value={form.gift_style} onValueChange={(v) => setForm({ ...form, gift_style: v })}>
                    <SelectTrigger className="rounded-none bg-white">
                      <SelectValue placeholder="Select style" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {GIFT_STYLES.map(style => (
                        <SelectItem key={style} value={style}>{style}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block text-sm">Gift Message</Label>
                  <Input
                    value={form.gift_message}
                    onChange={(e) => setForm({ ...form, gift_message: e.target.value })}
                    placeholder="Why this makes a perfect gift..."
                    className="rounded-none"
                  />
                </div>
              </div>
            </div>

            {/* Flags */}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2">
                <Checkbox 
                  checked={form.is_featured}
                  onCheckedChange={(c) => setForm({ ...form, is_featured: c })}
                />
                Featured
              </label>
              <label className="flex items-center gap-2">
                <Checkbox 
                  checked={form.is_trending}
                  onCheckedChange={(c) => setForm({ ...form, is_trending: c })}
                />
                Trending
              </label>
              <label className="flex items-center gap-2">
                <Checkbox 
                  checked={form.is_limited_edition}
                  onCheckedChange={(c) => setForm({ ...form, is_limited_edition: c })}
                />
                Limited Edition
              </label>
            </div>

            <div className="flex gap-4 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none bg-foreground text-primary-foreground">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Picker Modal */}
      {showImagePicker && (
        <ImagePicker
          selectedImages={form.images.filter(img => img)}
          onSelect={(images) => setForm({ ...form, images })}
          onClose={() => setShowImagePicker(false)}
          token={token}
        />
      )}
    </div>
  );
};

export default AdminProducts;
